"use client";

import translations from "@/lib/translations.json";
import {
  AlertTriangle,
  Building2,
  Calendar,
  DollarSign,
  FileSpreadsheet,
  FileText,
  MessageCircle,
} from "lucide-react";
import { useWebsiteLang } from "../context/website-lang-context";

function Tag({
  icon,
  label,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  className?: string;
}) {
  return (
    <div
      className={`absolute flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.02] px-3 py-1 text-white/90 backdrop-blur-sm sm:px-5 sm:py-3 ${className}`}
    >
      <span className="text-white/70">{icon}</span>
      <span className="text-[10px] sm-text-[10px]">{label}</span>
    </div>
  );
}

export default function ProblemBefore() {
  const { lang } = useWebsiteLang();
  const t = translations[lang].website.problem.before;

  return (
    <div className="relative w-full overflow-hidden bg-white/5 pb-6 pt-10">
      <div className="absolute left-1 top-1 sm:left-4 sm:top-4 z-10 flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/15 px-3 py-1.5 text-xs text-red-400 backdrop-blur-sm">
        <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
        {t.badge}
      </div>

      <div className="relative h-[320px] w-full overflow-hidden sm:h-[380px] md:h-[440px] lg:h-[500px]">
        <span className="absolute left-[12%] top-[68%] text-3xl text-white/20">
          ∿
        </span>
        <span className="absolute left-[65%] top-[50%] text-3xl text-white/20">
          ∿
        </span>
        <span className="absolute left-[70%] top-[20%] text-3xl text-white/20">
          ×
        </span>
        <span className="absolute left-[40%] top-[20%] text-3xl text-white/20">
          ×
        </span>

        <div className="absolute left-[15%] top-[40%] h-4 w-4 rounded-full border border-white/20" />
        <div className="absolute left-[38%] top-[55%] h-4 w-4 rounded-full border border-white/20" />
        <div className="absolute left-[75%] top-[32%] h-4 w-4 rounded-full border border-white/20" />
        <div className="absolute left-[48%] top-[82%] h-4 w-4 rounded-full border border-white/20" />

        <Tag
          className="left-[6%] top-[12%]"
          icon={<MessageCircle size={18} />}
          label={t.items.whatsapp}
        />
        <Tag
          className="right-[8%] top-[8%] rotate-3"
          icon={<FileSpreadsheet size={18} />}
          label={t.items.excel}
        />
        <Tag
          className="left-[42%] top-[42%] -rotate-3"
          icon={<Calendar size={18} />}
          label={t.items.calendars}
        />
        <Tag
          className="right-[5%] top-[58%] rotate-6"
          icon={<DollarSign size={18} />}
          label={t.items.payroll}
        />
        <Tag
          className="left-[10%] bottom-[11%] rotate-[-10deg]"
          icon={<Building2 size={18} />}
          label={t.items.banking}
        />
        <Tag
          className="right-[18%] bottom-[10%] -rotate-3"
          icon={<FileText size={18} />}
          label={t.items.invoicing}
        />
        <Tag
          className="left-[12%] bottom-[45%] rotate-6"
          icon={<FileText size={18} />}
          label={t.items.paper}
        />
      </div>

      <div
        className="absolute bottom-1 right-1 sm:bottom-3 sm:right-2 z-20 transition-all duration-700 delay-700"
        style={{ opacity: 1 }}
      >
        <div className="flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/15 px-3 py-1.5 text-xs font-medium text-red-400">
          <AlertTriangle size={12} />
          {t.footer}
        </div>
      </div>
    </div>
  );
}
